import { JobRecord } from './jobWorkspaceModel';
import { quoteJobId } from './clientWorkspaceFilters';

export type WorkspaceScope = 'Whole Company' | 'Selected Contact';
export const companyIdOf = (record: JobRecord | null | undefined): string | null => record?.company_id || record?.companyId || null;
export const isCompanyEntry = (record: JobRecord | null) => record?.workspaceEntity === 'company' || record?.industry !== undefined;
export const workspaceCompanyId = (entry: JobRecord | null) => isCompanyEntry(entry) ? entry?.id || null : companyIdOf(entry);
const emailOf = (record: JobRecord | null | undefined) => String(record?.email || record?.clientEmail || '').trim().toLowerCase();
export const sameContact = (record: JobRecord, contact: JobRecord | null) => Boolean(contact && (record.id === contact.id || (emailOf(contact) && emailOf(record) === emailOf(contact))));

export const scopedJobs = (jobs: JobRecord[], companyId: string | null, contact: JobRecord | null, scope: WorkspaceScope) =>
  jobs.filter(job => companyId ? companyIdOf(job) === companyId : !companyIdOf(job))
    .filter(job => scope === 'Whole Company' && companyId ? true : sameContact(job, contact));

/** A conflicting explicit company must never be rescued by an email/name/lead match. */
export const scopedQuotes = (quotes: JobRecord[], jobs: JobRecord[], companyId: string | null, contact: JobRecord | null, scope: WorkspaceScope) => {
  const companyJobs = scopedJobs(jobs, companyId, contact, companyId ? 'Whole Company' : 'Selected Contact');
  const selectedJobs = scopedJobs(jobs, companyId, contact, 'Selected Contact');
  const companyJobIds = new Set(companyJobs.map(job => job.id));
  const selectedJobIds = new Set(selectedJobs.map(job => job.id));
  return quotes.filter(quote => {
    const explicitCompany = companyIdOf(quote);
    const jobId = quoteJobId(quote);
    if (companyId) {
      if (explicitCompany && explicitCompany !== companyId) return false;
      if (explicitCompany !== companyId && (!jobId || !companyJobIds.has(jobId))) return false;
    } else if (explicitCompany || (jobId && !companyJobIds.has(jobId))) return false;
    if (scope === 'Whole Company' && companyId) return true;
    // A verified job relationship takes precedence over stale quote contact snapshots.
    if (jobId) return selectedJobIds.has(jobId);
    const quoteEmail = String(quote.client?.email || quote.client_email || '').trim().toLowerCase();
    return Boolean(emailOf(contact) && quoteEmail === emailOf(contact));
  });
};

export const companyWorkspaceEntry = (company: JobRecord) => ({ ...company, workspaceEntity: 'company' });
